/**
 * Deterministic default avatar fills (REDESIGN section 4): initials on a neutral cool gray, never a gradient.
 * The 8 tones differ only slightly, so avatars stay calm while neighbours remain distinguishable. Every tone
 * keeps white initials above 4.5:1. The export keeps its historic name and [from, to] shape; from equals to.
 * Raw hex is allowed here because these are fixed fills, not theme roles.
 */
export const AVATAR_GRADIENTS: ReadonlyArray<readonly [string, string]> = [
  ['#6B7280', '#6B7280'],
  ['#64748B', '#64748B'],
  ['#667085', '#667085'],
  ['#5F6B7A', '#5F6B7A'],
  ['#6B7383', '#6B7383'],
  ['#5E6A7D', '#5E6A7D'],
  ['#687284', '#687284'],
  ['#626B78', '#626B78'],
]

/** Stable 32 bit FNV-1a hash of a string. */
export function hashString(value: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

/** Index into AVATAR_GRADIENTS for a user id (numbers use modulo, strings are hashed). */
export function avatarGradientIndex(id: string | number | null | undefined): number {
  if (id === null || id === undefined || id === '') return 0
  if (typeof id === 'number' && Number.isFinite(id)) return Math.abs(Math.trunc(id)) % AVATAR_GRADIENTS.length
  return hashString(String(id)) % AVATAR_GRADIENTS.length
}

/** CSS background (a flat neutral colour) for the initials avatar of a user id. */
export function avatarGradient(id: string | number | null | undefined): string {
  return AVATAR_GRADIENTS[avatarGradientIndex(id)][0]
}

/** "Maria Gomez" -> "MG", "maria" -> "M", "  " -> "?". Uses the first and last word. */
export function getInitials(name: string | null | undefined, max = 2): string {
  const words = (name ?? '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (words.length === 0) return '?'
  const first = Array.from(words[0])[0] ?? ''
  if (max < 2 || words.length === 1) return first.toUpperCase()
  const last = Array.from(words[words.length - 1])[0] ?? ''
  return (first + last).toUpperCase()
}

/** LevelBadge size for an avatar size: 16 (24 to 32), 18 (40 to 48), 24 (64 to 96), 30 (128). */
export function levelBadgeSizeFor(avatarSize: number): 16 | 18 | 24 | 30 {
  if (avatarSize >= 112) return 30
  if (avatarSize >= 56) return 24
  if (avatarSize >= 36) return 18
  return 16
}
